import {
  createEntityAdapter,
  createSelector,
  EntityState,
} from '@reduxjs/toolkit';
import { apiSlice } from './api';
import { ResponseBody } from '../types/common';
import { RootState } from './store';
import { Trash, TrashParams } from '../types/trash';

const trashAdapter = createEntityAdapter({
  selectId: (data: any) => data.resource.seq,
});
const initialState = trashAdapter.getInitialState();

export const trashApiSlice = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    getTrashList: builder.query<EntityState<Trash>, TrashParams>({
      query: ({
        page = 0,
        search = '',
        size = 24,
        sortBy = 'delete_date',
        sortDir = 'DESC',
      }) => ({
        url: 'trash/detail',
        params: { page, search, size, sortBy, sortDir },
      }),
      serializeQueryArgs: ({ endpointName }) => {
        return endpointName;
      },
      merge: (currentCache, newItems, { arg }) => {
        if (arg.page && arg.page > 0) {
          currentCache.ids.push(...newItems.ids);
          currentCache.ids = [...new Set(currentCache.ids)];
          currentCache.entities = {
            ...currentCache.entities,
            ...newItems.entities,
          };
        } else {
          return newItems;
        }
      },
      forceRefetch({ currentArg, previousArg }) {
        return currentArg !== previousArg;
      },
      transformResponse: (res: ResponseBody) =>
        trashAdapter.setAll(initialState, res.data),
      providesTags: (result, error, arg) =>
        result
          ? [
              'Trash',
              ...result.ids.map((id) => ({
                type: 'Trash' as const,
                id,
              })),
            ]
          : ['Trash'],
    }),
    getTrash: builder.query({
      query: (seq) => `trash/detail/${seq}`,
    }),
    delTrash: builder.mutation({
      query: (data) => ({
        url: 'trash/edit/delete',
        method: 'POST',
        body: { data },
      }),
      invalidatesTags: ['Main', 'Settings'],
    }),
    delAllTrash: builder.mutation<ResponseBody, void>({
      query: () => ({
        url: 'trash/edit/delete/all',
        method: 'POST',
      }),
      invalidatesTags: ['Main', 'Settings'],
    }),
    restore: builder.mutation<
      ResponseBody,
      { seq: number; tag: 'Library' | 'Share' | 'Drive' }
    >({
      query: ({ seq }) => ({
        url: `trash/edit/restore/${seq}`,
        method: 'POST',
      }),
      invalidatesTags: (result, error, { tag }) => ['Main', 'Settings', tag],
    }),
  }),
});

export const {
  useGetTrashListQuery,
  useGetTrashQuery,
  useDelTrashMutation,
  useDelAllTrashMutation,
  useRestoreMutation,
} = trashApiSlice;

export const selectTrashResult = trashApiSlice.endpoints.getTrashList.select({
  page: 0,
});

const selectTrashData = createSelector(
  selectTrashResult,
  (trashResult) => trashResult.data
);

export const {
  selectAll: selectAllTrash,
  selectById: selectTrashById,
  selectIds: selectTrashIds,
  selectTotal: selectTotalTrashNum,
} = trashAdapter.getSelectors(
  (state: RootState) => selectTrashData(state) ?? initialState
);
